import classNames from "classnames";
import { LabelHTMLAttributes, memo, ReactNode } from "react";
import { Flex } from "../../flex";
import { Text } from "../../text";
import { textClasses } from "../../theme/typography";
import { DescriptionText, ErrorText, LabelText } from "../common/descriptors";
import * as redoTextInputCss from "./base-redo-input.module.css";
import {
  RedoInputSize,
  RedoInputState,
  sizeStyles,
  sizeToDescriptorTextProps,
  sizeToInputTextProps,
  sizeToPx,
} from "./base-redo-text-input";

export interface BaseRedoInputContainerProps {
  size?: RedoInputSize;
  state?: RedoInputState;
  label?: string;
  description?: string;
  errorText?: string;
  className?: string;
  children: ReactNode | ReactNode[];
  labelProps?: LabelHTMLAttributes<HTMLLabelElement>;
  dangerousStyleThatShouldOnlyBeUsedForMerchantBranding?: React.CSSProperties;
  fullWidth?: boolean;
  dynamicHeight?: boolean;
  uiSelector?: string;
  suffix?: string;
}

export const BaseRedoInputContainer = memo(function BaseRedoInputContainer({
  size = "sm",
  state = "default",
  label,
  description,
  errorText,
  className,
  labelProps,
  dangerousStyleThatShouldOnlyBeUsedForMerchantBranding,
  children,
  fullWidth,
  dynamicHeight,
  uiSelector,
  suffix,
}: BaseRedoInputContainerProps) {
  const descriptorFontSize = sizeToDescriptorTextProps[size];

  const error = state === "error";
  const disabled = state === "disabled";
  const readonly = state === "readonly";

  return (
    <Flex
      data-ui={uiSelector}
      dir="column"
      gap="sm"
      minW="none"
      w={fullWidth ? "full" : undefined}
    >
      <LabelText label={label} size={descriptorFontSize} />
      <Flex align="center" gap="none" p="none">
        <Flex
          align="center"
          as="label"
          bgColor="primary"
          className={classNames(
            disabled && redoTextInputCss.disabled,
            redoTextInputCss.inputWrapper,
            error && redoTextInputCss.error,
            !dynamicHeight && sizeStyles[size],
            readonly && redoTextInputCss.readonly,
            suffix && redoTextInputCss.hasSuffix,
            className,
          )}
          gap="none"
          grow={1}
          labelProps={labelProps}
          minW="none"
          px={readonly ? undefined : sizeToPx[size]}
          style={{
            backgroundColor: readonly ? "transparent" : undefined,
            ...dangerousStyleThatShouldOnlyBeUsedForMerchantBranding,
          }}
          textColor="primary"
        >
          {children}
        </Flex>
        {suffix && (
          <Flex
            align="center"
            alignSelf="stretch"
            bgColor="disabled"
            className={classNames(redoTextInputCss.suffix, sizeStyles[size])}
            pl="sm"
            pr="md"
            py="xs"
          >
            <Text
              className={classNames(textClasses(sizeToInputTextProps[size]))}
            >
              {suffix}
            </Text>
          </Flex>
        )}
      </Flex>
      <DescriptionText
        description={description}
        error={error}
        size={descriptorFontSize}
      />
      <ErrorText size={descriptorFontSize} text={errorText} />
    </Flex>
  );
});
